import getIsInCheckedMode from 'mini-mail-list-store/lib/utils/getIsInCheckedMode';
import getSelectedItemId from 'owa-mail-store/lib/store/selectors/getSelectedItemId';
import type Module from 'mini-common-schema/lib/schema/Module';
import ModuleSwitcher from 'mini-module-switcher/lib/components/ModuleSwitcher';
import React, { useRef } from 'react';
import styles from './MailSurface.scss';
import { A2HSCampaignProvider } from 'mini-a2hs/lib/components/A2HSCampaignProvider';
import { ActivityPane } from 'mini-activity-pane-view';
import { appendCatchAll, type MailRoutes, type MiscRoutes } from 'mini-routes';
import { getStore as getFolderStore } from 'mini-mail-folder-store/lib/store/Store';
import { HeaderHeroImage } from './HeaderHeroImage';
import { LazyMailSidePane, LazyMoveToFolderPane } from 'mini-mail-folder-view';
import { logUsage } from 'owa-analytics';
import { MailComposeWrapper } from 'mini-mail-compose-wrapper-view';
import { mailEventEmitter, TRIGGER_MAIL_LIST_SCROLL_TO_TOP } from 'mini-mail-event-emitters';
import { MailModuleBanner } from './MailModuleBanner';
import { MailMultiSelectFooter, MailMultiSelectHeader } from 'mini-mail-list-view';
import { MailReadingPaneWrapper } from 'mini-mail-reading-pane-wrapper-view';
import { MailSurfaceBody } from './MailSurfaceBody';
import { MailSurfaceHeader as MailSurfaceDefaultHeader } from './MailSurfaceHeader';
import { MiniScreen } from 'mini-screen';
import { observer } from 'mobx-react-lite';
import { Route, Routes } from 'mini-react-router-dom';
import { useHandleMailFolderNavigation } from '../hooks/useHandleMailFolderNavigation';

export const MailSurface = observer(function MailSurface() {
    const navigationButtonRef = useRef<HTMLElement>();

    useHandleMailFolderNavigation();

    const { showMoveToFolderPane } = getFolderStore();
    const selectedId = getSelectedItemId();

    const readingPaneElement = <MailReadingPaneWrapper />;

    return (
        <A2HSCampaignProvider>
            {showMoveToFolderPane && !selectedId && <LazyMoveToFolderPane />}

            <MiniScreen id={'MAIL_SURFACE'}>
                <MiniScreen.Header>
                    <HeaderHeroImage />
                    <MailSurfaceHeader />
                </MiniScreen.Header>
                <MiniScreen.Body className={!getIsInCheckedMode() && styles.bodyAndFooterContainer}>
                    <MailSurfaceBody />
                    <MailSurfaceFooter />
                </MiniScreen.Body>
            </MiniScreen>

            {/* This component renders mutually exclusive banners which are to be shown in Mail module */}
            <MailModuleBanner elementToFocusOnClose={navigationButtonRef} />
            <Routes>
                <Route path={'sidepane'} element={<LazyMailSidePane />} />
                <Route path={appendCatchAll('id/:id')} element={readingPaneElement} />
                <Route
                    path={appendCatchAll('id/:id/itemPartId/:itemPartId')}
                    element={readingPaneElement}
                />
                <Route path={appendCatchAll('itemId/:id')} element={readingPaneElement} />
                <Route
                    path={appendCatchAll('compose/:composeId')}
                    element={<MailComposeWrapper />}
                />
                <Route path={appendCatchAll('activity/:activityType')} element={<ActivityPane />} />
            </Routes>
        </A2HSCampaignProvider>
    );
});

const MailSurfaceHeader = () =>
    getIsInCheckedMode() ? <MailMultiSelectHeader /> : <MailSurfaceDefaultHeader />;

const MailSurfaceFooter = () => {
    const onActiveModuleClickAction = () => {
        mailEventEmitter.emit(TRIGGER_MAIL_LIST_SCROLL_TO_TOP, null);
        logUsage('ScrollToTopOnReClick', { source: 'MailModule' });
    };
    return getIsInCheckedMode() ? (
        <MailMultiSelectFooter />
    ) : (
        <ModuleSwitcher
            currentModule={0}
            onActiveModuleClickAction={onActiveModuleClickAction}
            className={styles.moduleSwitcher}
        />
    );
};
